import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Skeleton,
    Typography,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import ProfileFlyer from "../../components/ProfileFlyer";
import ProfileBusinessCard from "../../components/ProfileBusinessCard";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { Link as RouterLink } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { DashboardService } from "../../services/DashboardService";
import { CalendarService } from "../../services/CalendarService";
import { useKeycloak } from "@react-keycloak/web";
import PropTypes from "prop-types";
import { getSlugFromLabel } from "./profile";
import { getBaseUrl, buildPublicProfileUrl } from "../../utils/publicProfileUrl";

dayjs.extend(utc);

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const timeRanges = [
    { label: "7 days", value: 7, granularity: "day" },
    { label: "30 days", value: 30, granularity: "day" },
    { label: "90 days", value: 90, granularity: "week" },
    { label: "12 months", value: 365, granularity: "month" },
];

const metrics = [
    { label: "Revenue", value: "revenue" },
    { label: "Appointments", value: "appointments" },
    { label: "Unique Clients", value: "uniqueClients" },
];

const paymentFilters = [
    { label: "All", value: "all" },
    { label: "Prepaid", value: "prepaid" },
    { label: "Postpaid", value: "postpaid" },
];

function getDateRangeForDays(days) {
    const to = dayjs().utc().endOf('day');
    const from = to.subtract(days, 'day').startOf('day');
    return { from: from.toISOString(), to: to.toISOString() };
}

function formatAxisLabel(date, granularity) {
    const d = dayjs(date);
    switch (granularity) {
        case "day":
            return d.format("MMM D");
        case "week":
            return `Week of ${d.format("MMM D")}`;
        case "month":
            return d.format("MMM YYYY");
        default:
            return d.format("MMM D");
    }
}

function formatTooltipValue(value, metric) {
    if (metric === "revenue") {
        return CURRENCY_FORMATTER.format(value);
    }
    return value.toString();
}

function getSeries(data, metric, paymentFilter) {
    if (!data || !data.series || data.series.length === 0) {
        return [];
    }

    return data.series.map(period => {
        let value = 0;
        if (metric === "revenue") {
            if (paymentFilter === "all") {
                value = (period.prepaidRevenue || 0) + (period.postpaidRevenue || 0);
            } else if (paymentFilter === "prepaid") {
                value = period.prepaidRevenue || 0;
            } else if (paymentFilter === "postpaid") {
                value = period.postpaidRevenue || 0;
            }
        } else if (metric === "appointments") {
            if (paymentFilter === "all") {
                value = period.appointments || 0;
            } else if (paymentFilter === "prepaid") {
                value = period.prepaidAppointments || 0;
            } else if (paymentFilter === "postpaid") {
                value = period.postpaidAppointments || 0;
            }
        } else if (metric === "uniqueClients") {
            value = period.uniqueClients || 0;
        }

        return {
            periodStart: period.periodStart,
            value: value,
        };
    });
}

function mergeRevenueData(revenueData, statsData) {
    // Merge prepaid (from PaymentService) and postpaid (from CalendarService) revenue
    // Handle partial availability: if one is missing, return the other
    if (!revenueData && !statsData) {
        return null;
    }

    if (!revenueData) {
        // Only stats available; extract postpaid revenue
        return {
            totals: statsData.totals ? {
                postpaidRevenue: statsData.totals.postpaidRevenue || 0,
                prepaidRevenue: undefined,
            } : null,
            series: statsData.series || [],
        };
    }

    if (!statsData) {
        // Only revenue data available; extract prepaid revenue
        return {
            totals: revenueData.totals ? {
                prepaidRevenue: revenueData.totals.prepaidRevenue || 0,
                postpaidRevenue: undefined,
            } : null,
            series: revenueData.series || [],
        };
    }

    // Both available: merge by periodStart using normalized time values
    const merged = { totals: {}, series: [] };

    // Merge totals
    if (revenueData.totals && statsData.totals) {
        merged.totals = {
            prepaidRevenue: revenueData.totals.prepaidRevenue || 0,
            postpaidRevenue: statsData.totals.postpaidRevenue || 0,
        };
    }

    // Merge series by matching periodStart on normalized timestamps
    if (revenueData.series && statsData.series) {
        // Helper to safely parse a periodStart and get normalized timestamp
        const getNormalizedTime = (periodStart) => {
            if (!periodStart) return null;
            try {
                const date = new Date(periodStart);
                // Guard against invalid dates
                if (isNaN(date.getTime())) return null;
                return date.getTime();
            } catch {
                return null;
            }
        };

        // Create a map of statsData series by normalized time for O(1) lookup
        const statsMap = new Map();
        statsData.series.forEach(s => {
            const normalizedTime = getNormalizedTime(s.periodStart);
            if (normalizedTime !== null) {
                statsMap.set(normalizedTime, s);
            }
        });

        // Merge: iterate through revenueData, match with statsData, add unmatched stats
        const processedTimes = new Set();
        const mergedEntries = new Map(); // key: normalized time, value: merged entry

        // Process revenue periods and match with stats
        revenueData.series.forEach(revenuePeriod => {
            const normalizedTime = getNormalizedTime(revenuePeriod.periodStart);
            if (normalizedTime === null) return; // Skip unparseable entries

            const statsPeriod = statsMap.get(normalizedTime);
            processedTimes.add(normalizedTime);

            // Prefer stats periodStart if present, else revenue periodStart
            const canonicalPeriodStart = statsPeriod?.periodStart || revenuePeriod.periodStart;

            mergedEntries.set(normalizedTime, {
                periodStart: canonicalPeriodStart,
                prepaidRevenue: revenuePeriod.prepaidRevenue || 0,
                postpaidRevenue: statsPeriod?.postpaidRevenue || 0,
            });
        });

        // Add any stats periods that didn't match a revenue period
        statsData.series.forEach(statsPeriod => {
            const normalizedTime = getNormalizedTime(statsPeriod.periodStart);
            if (normalizedTime === null) return; // Skip unparseable entries

            if (!processedTimes.has(normalizedTime)) {
                mergedEntries.set(normalizedTime, {
                    periodStart: statsPeriod.periodStart,
                    prepaidRevenue: 0,
                    postpaidRevenue: statsPeriod.postpaidRevenue || 0,
                });
            }
        });

        // Convert map entries to sorted array by normalized time (ascending)
        merged.series = Array.from(mergedEntries.entries())
            .sort((a, b) => a[0] - b[0]) // Sort by normalized time
            .map(entry => entry[1]); // Extract the merged entry
    }

    return merged;
}

function getTotals(data, metric, paymentFilter) {
    if (!data || !data.totals) {
        return null;
    }

    const totals = data.totals;
    if (metric === "revenue") {
        if (paymentFilter === "all") {
            return (totals.prepaidRevenue || 0) + (totals.postpaidRevenue || 0);
        } else if (paymentFilter === "prepaid") {
            return totals.prepaidRevenue || 0;
        } else if (paymentFilter === "postpaid") {
            return totals.postpaidRevenue || 0;
        }
    } else if (metric === "appointments") {
        if (paymentFilter === "all") {
            return totals.appointments || 0;
        } else if (paymentFilter === "prepaid") {
            return totals.prepaidAppointments || 0;
        } else if (paymentFilter === "postpaid") {
            return totals.postpaidAppointments || 0;
        }
    } else if (metric === "uniqueClients") {
        return totals.uniqueClients || 0;
    }

    return 0;
}

function HeroCard({ value, metric, delta, isLoading, isUnavailable }) {
    const formatValue = () => {
        if (value === null || value === undefined) {
            return "Unavailable";
        }
        if (metric === "revenue") {
            return CURRENCY_FORMATTER.format(value);
        }
        return value.toString();
    };

    const getDeltaColor = () => {
        if (delta === null || delta === undefined || !isFinite(delta)) {
            return "common.white";
        }
        return "common.white";
    };

    const getDeltaLabel = () => {
        if (delta === null || delta === undefined || !isFinite(delta)) {
            return null;
        }
        const sign = delta >= 0 ? "+" : "";
        return `${sign}${delta.toFixed(1)}%`;
    };

    return (
        <Card sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "white",
            mb: 3,
        }}>
            <CardContent>
                {isLoading ? (
                    <>
                        <Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.2)', mb: 2 }} height={40} />
                        <Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.2)', mb: 2 }} height={24} />
                    </>
                ) : (
                    <>
                        <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 1 }}>
                            {metric === "revenue" ? "Total Revenue" : metric === "appointments" ? "Total Appointments" : "Total Unique Clients"}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, opacity: isUnavailable ? 0.6 : 1 }}>
                            {formatValue()}
                        </Typography>
                        {isUnavailable && (
                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 1 }}>
                                (temporarily unavailable)
                            </Typography>
                        )}
                        {getDeltaLabel() && !isUnavailable && (
                            <Typography variant="body2" sx={{ color: getDeltaColor() }}>
                                {getDeltaLabel()} vs previous period
                            </Typography>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

HeroCard.propTypes = {
    value: PropTypes.number,
    metric: PropTypes.string.isRequired,
    delta: PropTypes.number,
    isLoading: PropTypes.bool.isRequired,
    isUnavailable: PropTypes.bool,
};

function StatCard({ label, value, metric, isLoading }) {
    const formatValue = () => {
        if (value === null || value === undefined) {
            return "Unavailable";
        }
        if (metric === "revenue") {
            return CURRENCY_FORMATTER.format(value);
        }
        return value.toString();
    };

    return (
        <Card>
            <CardContent>
                {isLoading ? (
                    <>
                        <Skeleton sx={{ mb: 1 }} height={20} width="70%" />
                        <Skeleton height={32} />
                    </>
                ) : (
                    <>
                        <Typography color="textSecondary" gutterBottom>
                            {label}
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: value === null ? 0.6 : 1 }}>
                            {formatValue()}
                        </Typography>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

StatCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    metric: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
};

function UpcomingAppointments({ providerId, isLoading, error }) {
    const [appointments, setAppointments] = useState([]);
    const { keycloak } = useKeycloak();

    useEffect(() => {
        if (!providerId || !keycloak) return;

        CalendarService.keycloak = keycloak;
        const fetchAppointments = async () => {
            try {
                const schedule = await CalendarService.getAppointments(providerId);
                if (schedule && schedule.appointments && Array.isArray(schedule.appointments)) {
                    const now = dayjs();
                    const future = schedule.appointments
                        .filter(apt => dayjs(apt.start).isAfter(now))
                        .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)))
                        .slice(0, 5);
                    setAppointments(future);
                }
            } catch (err) {
                console.error("Failed to fetch appointments:", err);
            }
        };

        fetchAppointments();
    }, [providerId, keycloak]);

    const formatAppointmentTime = (start) => {
        const apt = dayjs(start);
        const today = dayjs().startOf('day');
        const tomorrow = today.add(1, 'day');

        if (apt.isSame(today, 'day')) {
            return `Today, ${apt.format('h:mm A')}`;
        } else if (apt.isSame(tomorrow, 'day')) {
            return `Tomorrow, ${apt.format('h:mm A')}`;
        } else {
            return apt.format('MMM D, h:mm A');
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[1, 2, 3].map(i => (
                    <Skeleton key={i} height={60} />
                ))}
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                Failed to load appointments. Please try again.
            </Alert>
        );
    }

    if (appointments.length === 0) {
        return (
            <Typography variant="body2" color="textSecondary">
                No upcoming appointments scheduled.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {appointments.map((apt, idx) => (
                <Card key={idx} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {apt.clientName || 'Client'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {apt.service || 'Service'}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', ml: 1 }}>
                            {formatAppointmentTime(apt.start)}
                        </Typography>
                    </Box>
                </Card>
            ))}
        </Box>
    );
}

UpcomingAppointments.propTypes = {
    providerId: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.bool.isRequired,
};

export function DashboardTab({ provider, onAddService }) {
    const { keycloak } = useKeycloak();
    const providerId = provider?.id;

    const [timeRange, setTimeRange] = useState(30);
    const [metric, setMetric] = useState("revenue");
    const [paymentFilter, setPaymentFilter] = useState("all");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentData, setCurrentData] = useState(null);
    const [previousData, setPreviousData] = useState(null);
    const [revenueData, setRevenueData] = useState(null);
    const [showFlyerDialog, setShowFlyerDialog] = useState(false);
    const [showBusinessCardDialog, setShowBusinessCardDialog] = useState(false);
    const [flyerPdfError, setFlyerPdfError] = useState("");

    const flyerRef = useRef();
    const businessCardRef = useRef();

    const currentGranularity = timeRanges.find(r => r.value === timeRange)?.granularity || "day";
    const isUniqueClientsMetric = metric === "uniqueClients";

    // Dialog handlers
    const openFlyerDialog = useCallback(() => {
        setShowFlyerDialog(true);
        setFlyerPdfError("");
    }, []);

    const closeFlyerDialog = useCallback(() => {
        setShowFlyerDialog(false);
        setFlyerPdfError("");
    }, []);

    const openBusinessCardDialog = useCallback(() => {
        setShowBusinessCardDialog(true);
    }, []);

    const closeBusinessCardDialog = useCallback(() => {
        setShowBusinessCardDialog(false);
    }, []);

    const printFlyer = useCallback(() => {
        if (flyerRef.current && flyerRef.current.print) {
            flyerRef.current.print();
        }
    }, []);

    const saveflyerAsPDF = useCallback(() => {
        if (flyerRef.current && flyerRef.current.saveAsPDF) {
            flyerRef.current.saveAsPDF();
        }
    }, []);

    const handleFlyerPdfError = useCallback((errorMessage) => {
        setFlyerPdfError(errorMessage);
    }, []);

    const printBusinessCard = useCallback(() => {
        if (businessCardRef.current && businessCardRef.current.print) {
            businessCardRef.current.print();
        }
    }, []);

    const saveBusinessCardAsPDF = useCallback(() => {
        if (businessCardRef.current && businessCardRef.current.saveAsPDF) {
            businessCardRef.current.saveAsPDF();
        }
    }, []);

    const handleBusinessCardError = useCallback((errorMessage) => {
        // Could be used to display errors above actions if needed
        console.error('Business card error:', errorMessage);
    }, []);

    // Fetch data
    useEffect(() => {
        if (!providerId || !keycloak) return;

        DashboardService.keycloak = keycloak;
        CalendarService.keycloak = keycloak;

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                // Get current period date range
                const { from: currentFrom, to: currentTo } = getDateRangeForDays(timeRange);

                // Get previous period date range (same duration before current)
                const previousTo = dayjs(currentFrom).subtract(1, 'day').utc().endOf('day');
                const previousFrom = previousTo.subtract(timeRange, 'day').startOf('day');

                // Use Promise.allSettled for graceful degradation: if one call fails, others still render
                const results = await Promise.allSettled([
                    DashboardService.getProviderStats(providerId, currentFrom, currentTo, currentGranularity),
                    DashboardService.getProviderStats(providerId, previousFrom.toISOString(), previousTo.toISOString(), currentGranularity),
                    DashboardService.getProviderRevenue(providerId, currentFrom, currentTo, currentGranularity),
                ]);

                // Extract results with fallbacks
                const currentResult = results[0];
                const previousResult = results[1];
                const revenueResult = results[2];

                // If current stats fail, show full error (it's required for the dashboard)
                if (currentResult.status === 'rejected') {
                    console.error("Failed to fetch current stats:", currentResult.reason);
                    setError("Failed to load dashboard data. Please try again.");
                    return;
                }

                // Current data is required; set it
                setCurrentData(currentResult.value);

                // Previous data is optional for deltas; only set if successful
                if (previousResult.status === 'fulfilled') {
                    setPreviousData(previousResult.value);
                } else {
                    console.warn("Failed to fetch previous stats for delta calculation:", previousResult.reason);
                    setPreviousData(null);
                }

                // Revenue data is optional; only set if successful
                if (revenueResult.status === 'fulfilled') {
                    setRevenueData(revenueResult.value);
                } else {
                    console.warn("Failed to fetch revenue data:", revenueResult.reason);
                    setRevenueData(null);
                }
            } catch (err) {
                console.error("Failed to fetch dashboard data:", err);
                setError("Failed to load dashboard data. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [providerId, keycloak, timeRange, currentGranularity]);

    const getMergedRevenueValue = useCallback((stats, revenue, filter) => {
        // Helper to get revenue value from merged sources
        const merged = mergeRevenueData(revenue, stats);
        return getTotals(merged, "revenue", filter);
    }, []);

    const getCurrentMetricValue = useCallback(() => {
        if (metric === "revenue") {
            // For revenue, merge both data sources: prepaid from PaymentService, postpaid from CalendarService
            return getMergedRevenueValue(currentData, revenueData, paymentFilter);
        }
        return getTotals(currentData, metric, paymentFilter);
    }, [metric, paymentFilter, currentData, revenueData, getMergedRevenueValue]);

    const getPreviousMetricValue = useCallback(() => {
        if (metric === "revenue") {
            // For revenue delta: merge postpaid from previous stats with prepaid (unavailable)
            // We don't fetch previous revenueData, so prepaid is treated as unavailable for the previous period
            return getMergedRevenueValue(previousData, null, paymentFilter);
        }
        return getTotals(previousData, metric, paymentFilter);
    }, [metric, paymentFilter, previousData, getMergedRevenueValue]);

    const calculateDelta = useCallback(() => {
        const current = getCurrentMetricValue();
        const previous = getPreviousMetricValue();

        if (previous === 0) {
            return current > 0 ? 100 : 0;
        }

        return ((current - previous) / previous) * 100;
    }, [getCurrentMetricValue, getPreviousMetricValue]);

    const getChartData = useCallback(() => {
        let data;
        if (metric === "revenue") {
            // For revenue chart, merge both data sources
            data = mergeRevenueData(revenueData, currentData);
        } else {
            data = currentData;
        }
        if (!data || !data.series) return [];

        return getSeries(data, metric, paymentFilter).map(item => ({
            ...item,
            display: formatAxisLabel(item.periodStart, currentGranularity),
        }));
    }, [metric, paymentFilter, currentData, revenueData, currentGranularity]);

    const isEmptyState = currentData?.totals &&
        Object.values(currentData.totals).every(v => v === 0);

    const currentValue = getCurrentMetricValue();
    const delta = calculateDelta();
    const chartData = getChartData();
    const isRevenueUnavailable = metric === "revenue" && !revenueData;

    // Share section with QR code and action buttons (renders in all states when friendlyUrl exists)
    const shareSection = provider?.friendlyUrl && (
        <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Scan to open your public profile
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                <Box data-testid="profile-qr-code">
                    <QRCodeSVG
                        value={buildPublicProfileUrl(getBaseUrl(), provider.friendlyUrl)}
                        size={180}
                        level="H"
                        marginSize={4}
                        imageSettings={{
                            src: require('../../assets/svgs/PairSelfCareIcon.png'),
                            height: 36,
                            width: 36,
                            excavate: true
                        }}
                        title="Scan to open your public profile"
                    />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                        variant="contained"
                        onClick={openFlyerDialog}
                        data-testid="create-flyer-button"
                    >
                        Create Flyer
                    </Button>
                    <Button
                        variant="contained"
                        onClick={openBusinessCardDialog}
                        data-testid="create-business-card-button"
                    >
                        Create Business Card
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    // Dialogs for flyer and business card printing (must be available in all states)
    const documentDialogs = (
        <>
            {/* Flyer Dialog */}
            <Dialog
                open={showFlyerDialog}
                onClose={closeFlyerDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '90vh',
                    }
                }}
            >
                <DialogTitle>Print Flyer Preview</DialogTitle>
                <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', overflow: 'auto' }}>
                    <style>
                        {`
                            @page {
                                size: Letter;
                                margin: 0;
                            }
                            .print-flyer-container {
                                max-width: 100%;
                                overflow-x: auto;
                                display: flex;
                                justify-content: center;
                            }
                            .print-flyer-wrapper {
                                transform-origin: top center;
                                flex-shrink: 0;
                            }
                            @media print {
                                body * {
                                    visibility: hidden !important;
                                }
                                .print-flyer-container,
                                .print-flyer-container * {
                                    visibility: visible !important;
                                }
                                .print-flyer-container {
                                    position: absolute !important;
                                    left: 0 !important;
                                    top: 0 !important;
                                    width: 100% !important;
                                    padding: 0 !important;
                                    margin: 0 !important;
                                    display: block !important;
                                    overflow: visible !important;
                                }
                                .print-flyer-wrapper {
                                    transform: none !important;
                                }
                                .MuiDialog-root,
                                .MuiDialog-container,
                                .MuiDialog-paper,
                                .MuiPaper-root,
                                .MuiDialogContent-root {
                                    position: static !important;
                                    transform: none !important;
                                    overflow: visible !important;
                                    max-height: none !important;
                                    height: auto !important;
                                    box-shadow: none !important;
                                    padding: 0 !important;
                                    margin: 0 !important;
                                    display: block !important;
                                }
                            }
                        `}
                    </style>
                    <Box className="print-flyer-container">
                        <Box className="print-flyer-wrapper" sx={{ p: 2 }}>
                            <ProfileFlyer
                                ref={flyerRef}
                                provider={provider}
                                publicProfileUrl={buildPublicProfileUrl(getBaseUrl(), provider.friendlyUrl)}
                                onError={handleFlyerPdfError}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                {flyerPdfError && (
                    <Box sx={{ color: '#d32f2f', fontSize: '14px', padding: '10px 16px', backgroundColor: '#ffebee', margin: '8px 16px 0 16px', borderRadius: '4px' }}>
                        {flyerPdfError}
                    </Box>
                )}
                <DialogActions>
                    <Button onClick={closeFlyerDialog}>Close</Button>
                    <Button onClick={printFlyer} variant="contained">
                        Print
                    </Button>
                    <Button onClick={saveflyerAsPDF} variant="contained" color="secondary">
                        Save As PDF
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Business Card Dialog */}
            <Dialog
                open={showBusinessCardDialog}
                onClose={closeBusinessCardDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Print Business Card Preview</DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    <style>
                        {`
                            @page {
                                size: 3.5in 2in;
                                margin: 0;
                            }
                            @media print {
                                body * {
                                    visibility: hidden !important;
                                }
                                .print-business-card-container,
                                .print-business-card-container * {
                                    visibility: visible !important;
                                }
                                .print-business-card-container {
                                    position: absolute !important;
                                    left: 0 !important;
                                    top: 0 !important;
                                    width: 100% !important;
                                    margin: 0 !important;
                                }
                                .MuiDialog-root,
                                .MuiDialog-container,
                                .MuiDialog-paper,
                                .MuiPaper-root,
                                .MuiDialogContent-root {
                                    position: static !important;
                                    transform: none !important;
                                    overflow: visible !important;
                                    max-height: none !important;
                                    height: auto !important;
                                    box-shadow: none !important;
                                    margin: 0 !important;
                                }
                            }
                        `}
                    </style>
                    <Box className="print-business-card-container" sx={{ p: 2 }}>
                        <ProfileBusinessCard
                            ref={businessCardRef}
                            provider={provider}
                            publicProfileUrl={buildPublicProfileUrl(getBaseUrl(), provider.friendlyUrl)}
                            onError={handleBusinessCardError}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeBusinessCardDialog}>Close</Button>
                    <Button onClick={printBusinessCard} variant="contained">
                        Print
                    </Button>
                    <Button onClick={saveBusinessCardAsPDF} variant="contained" color="secondary">
                        Save As PDF
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );

    if (error && !isLoading) {
        return (
            <Box sx={{ p: '5px', pb: '25px' }}>
                <Alert severity="error">
                    {error}
                    <Button onClick={() => window.location.reload()} sx={{ mt: 1 }} size="small" variant="contained">
                        Reload Page
                    </Button>
                </Alert>
                {shareSection}
                {documentDialogs}
            </Box>
        );
    }

    if (isEmptyState && !isLoading) {
        return (
            <Box sx={{ p: '5px', pb: '25px' , textAlign: 'center' }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Your dashboard will fill in as clients book with you.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                        To start taking bookings,{' '}
                        {onAddService ? (
                            <Link
                                component="button"
                                onClick={onAddService}
                                sx={{ cursor: 'pointer' }}
                            >
                                Add a Service
                            </Link>
                        ) : (
                            <Link
                                component={RouterLink}
                                to={`/profile?tab=${getSlugFromLabel("Services")}`}
                                sx={{ cursor: 'pointer' }}
                            >
                                Add a Service
                            </Link>
                        )}
                        , set your availability in{' '}
                        <Link
                            component={RouterLink}
                            to={`/profile?tab=${getSlugFromLabel("Calendar")}`}
                            sx={{ cursor: 'pointer' }}
                        >
                            Calendar
                        </Link>
                        , and set up payments under{' '}
                        <Link
                            component={RouterLink}
                            to={`/profile?tab=${getSlugFromLabel("Payments")}`}
                            sx={{ cursor: 'pointer' }}
                        >
                            Payments
                        </Link>
                        .
                    </Typography>
                </Box>
                {shareSection}
                {documentDialogs}
            </Box>
        );
    }

    return (
        <Box sx={{ p: '5px', pb: '25px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Business Dashboard
            </Typography>

            {shareSection}

            {/* Controls */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Time Range</InputLabel>
                        <Select
                            value={timeRange}
                            label="Time Range"
                            onChange={(e) => setTimeRange(e.target.value)}
                        >
                            {timeRanges.map(range => (
                                <MenuItem key={range.value} value={range.value}>
                                    {range.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Metric</InputLabel>
                        <Select
                            value={metric}
                            label="Metric"
                            onChange={(e) => setMetric(e.target.value)}
                        >
                            {metrics.map(m => (
                                <MenuItem key={m.value} value={m.value}>
                                    {m.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                {!isUniqueClientsMetric && (
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Payment Type</InputLabel>
                            <Select
                                value={paymentFilter}
                                label="Payment Type"
                                onChange={(e) => setPaymentFilter(e.target.value)}
                            >
                                {paymentFilters.map(pf => (
                                    <MenuItem key={pf.value} value={pf.value}>
                                        {pf.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
            </Grid>

            {/* Hero Card */}
            <HeroCard
                value={currentValue}
                metric={metric}
                delta={delta}
                isLoading={isLoading}
                isUnavailable={isRevenueUnavailable}
            />

            {/* Stat Cards Row */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {metric !== "appointments" && (
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            label="Total Appointments"
                            value={getTotals(currentData, "appointments", paymentFilter)}
                            metric="appointments"
                            isLoading={isLoading}
                        />
                    </Grid>
                )}
                {metric !== "uniqueClients" && (
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            label="Unique Clients"
                            value={getTotals(currentData, "uniqueClients", paymentFilter)}
                            metric="uniqueClients"
                            isLoading={isLoading}
                        />
                    </Grid>
                )}
                {metric !== "revenue" && (
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            label="Total Revenue"
                            value={getMergedRevenueValue(currentData, revenueData, paymentFilter)}
                            metric="revenue"
                            isLoading={isLoading}
                        />
                    </Grid>
                )}
            </Grid>

            {/* Main Chart */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        {metric === "revenue" ? "Revenue Over Time" : metric === "appointments" ? "Appointments Over Time" : "Unique Clients Over Time"}
                    </Typography>
                    {isLoading ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : chartData.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                                No bookings in this period yet.{' '}
                                {onAddService ? (
                                    <Link
                                        component="button"
                                        onClick={onAddService}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        Add a Service
                                    </Link>
                                ) : (
                                    <Link
                                        component={RouterLink}
                                        to={`/profile?tab=${getSlugFromLabel("Services")}`}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        Add a Service
                                    </Link>
                                )}
                                , set your availability in{' '}
                                <Link
                                    component={RouterLink}
                                    to={`/profile?tab=${getSlugFromLabel("Calendar")}`}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    Calendar
                                </Link>
                                , and your bookings will show up here.
                            </Typography>
                        </Box>
                    ) : (
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={chartData}>
                                <defs>
                                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#1976d2" stopOpacity={0.1} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="display"
                                    tick={{ fontSize: 12 }}
                                />
                                <YAxis
                                    tick={{ fontSize: 12 }}
                                />
                                <Tooltip
                                    formatter={(value) => formatTooltipValue(value, metric)}
                                    labelFormatter={(label) => label}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#1976d2"
                                    strokeWidth={2}
                                    fillOpacity={1}
                                    fill="url(#colorValue)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>

            {/* Upcoming Appointments Section */}
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Upcoming Appointments
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <UpcomingAppointments
                        providerId={providerId}
                        isLoading={isLoading}
                        error={!!error}
                    />
                </Grid>
            </Grid>

            {documentDialogs}
        </Box>
    );
}

DashboardTab.propTypes = {
    provider: PropTypes.shape({
        id: PropTypes.string.isRequired,
        friendlyUrl: PropTypes.string,
    }).isRequired,
    onAddService: PropTypes.func,
};


